import React, { FC, MutableRefObject, useCallback } from 'react';
import { Box, Popover, PopoverOrigin, SxProps, Theme, Typography, MenuItem } from '@mui/material';
import { CheckIcon, clsx, useStyles } from '@sweatpals/react-ui-kit';
import { authStore } from '../../../stores';
import UserAvatar from '../../images/UserAvatar';
import { DashboardIcon, LogOutIcon } from '../../icons';
import { userProfileMenuStyles } from './user-profile-menu.styles';
import Link from 'next/link';
import { observer } from 'mobx-react-lite';
import useLink from '../../../utils/hooks/useLink';

export interface IUserProfileMenu {
	open: boolean;
	anchorRef: MutableRefObject<HTMLElement | null>;
	anchorOrigin?: PopoverOrigin;
	transformOrigin?: PopoverOrigin;
	paperSx?: SxProps<Theme>;
	onClose(): void;
}

const UserProfileMenu: FC<IUserProfileMenu> = observer((props) => {
	const {
		open,
		anchorRef,
		anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
		transformOrigin = { vertical: 'top', horizontal: 'right' },
		paperSx,
		onClose,
	} = props;
	const { user: currentUser } = authStore;

	const { makeDashboardLink } = useLink();
	const styles = useStyles(userProfileMenuStyles);

	const onLogout = useCallback(() => {
		authStore.logout();
		onClose();
	}, [onClose]);

	return (
		<Popover
			open={open}
			anchorEl={anchorRef.current}
			onClose={onClose}
			anchorOrigin={anchorOrigin}
			transformOrigin={transformOrigin}
			PaperProps={{
				sx: clsx(styles.paper, paperSx),
			}}
		>
			<Box>
				<Box sx={styles.account}>
					{currentUser && <UserAvatar user={currentUser} />}
					<Box display="flex" flexDirection="column" gap="4px" flex={1}>
						<Typography sx={styles.accountTitle}>
							Current User
						</Typography>
						<Typography sx={styles.accountName}>
							{currentUser?.fullName}
						</Typography>
					</Box>
					<Box sx={styles.accountIcon}>
						<CheckIcon />
					</Box>
				</Box>
				<Link
					href={makeDashboardLink()}
					passHref
					style={{
						textDecoration: 'none',
					}}
				>
					<MenuItem sx={styles.menuItem} onClick={onClose}>
						<DashboardIcon />
						<Typography variant="inherit">
							Switch to Dashboard
						</Typography>
					</MenuItem>
				</Link>
				<MenuItem sx={styles.menuItem} onClick={onLogout}>
					<LogOutIcon />
					<Typography variant="inherit">
						Log Out
					</Typography>
				</MenuItem>
			</Box>
		</Popover>
	);
});

export default UserProfileMenu;
